import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Loader2, Trash2, Mail, Phone, Package, MapPin, RefreshCw, Inbox } from 'lucide-react';

const STATUSES = ['new', 'contacted', 'quoted', 'closed'];
const statusColor = {
  new: 'bg-ktc-gold/20 text-ktc-gold',
  contacted: 'bg-blue-100 text-blue-700',
  quoted: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-ktc-charcoal/10 text-ktc-charcoal/60',
};

const EnquiriesPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/enquiries'); setItems(data); } catch (e) {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
    try { await api.patch(`/admin/enquiries/${id}`, { status }); } catch (e) { load(); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this enquiry?')) return;
    setItems((prev) => prev.filter((x) => x.id !== id));
    try { await api.delete(`/admin/enquiries/${id}`); } catch (e) { load(); }
  };

  const shown = items.filter((x) => filter === 'all' || x.type === filter);
  const fmt = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

  if (loading) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-ktc-green" size={32} /></div>;

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-2">
          {['all', 'quote', 'contact'].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 rounded-full text-sm font-medium capitalize transition-colors ${filter === f ? 'bg-ktc-green text-ktc-ivory' : 'bg-ktc-cream/70 text-ktc-charcoal/75 hover:bg-ktc-cream'}`}>{f === 'all' ? 'All' : f}</button>
          ))}
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 text-sm text-ktc-green font-medium"><RefreshCw size={15} /> Refresh</button>
      </div>

      {shown.length === 0 ? (
        <div className="text-center py-20 text-ktc-charcoal/50"><Inbox className="mx-auto mb-3" size={40} /> No enquiries yet.</div>
      ) : (
        <div className="space-y-4">
          {shown.map((e) => (
            <div key={e.id} className="bg-ktc-ivoryLight border border-ktc-cream rounded-xl p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full ${e.type === 'quote' ? 'bg-ktc-green/10 text-ktc-green' : 'bg-ktc-gold/15 text-ktc-gold'}`}>{e.type}</span>
                    <h3 className="font-serif text-xl font-semibold text-ktc-green">{e.name}</h3>
                  </div>
                  <div className="text-xs text-ktc-charcoal/50 mt-1">{fmt(e.created_at)}{e.company ? ` · ${e.company}` : ''}{e.country ? ` · ${e.country}` : ''}</div>
                </div>
                <div className="flex items-center gap-2">
                  <select value={e.status} onChange={(ev) => setStatus(e.id, ev.target.value)} className={`text-xs font-semibold rounded-full px-3 py-1.5 border-0 cursor-pointer ${statusColor[e.status] || statusColor.new}`}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => remove(e.id)} className="h-8 w-8 grid place-items-center rounded-md text-red-600 hover:bg-red-50"><Trash2 size={16} /></button>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 mt-4 text-sm">
                <span className="flex items-center gap-2 text-ktc-charcoal/75"><Mail size={14} className="text-ktc-gold" /> <a href={`mailto:${e.email}`} className="hover:text-ktc-green">{e.email}</a></span>
                {e.phone && <span className="flex items-center gap-2 text-ktc-charcoal/75"><Phone size={14} className="text-ktc-gold" /> {e.phone}</span>}
                {e.product && <span className="flex items-center gap-2 text-ktc-charcoal/75"><Package size={14} className="text-ktc-gold" /> {e.product}{e.quantity ? ` (${e.quantity} T)` : ''}</span>}
                {(e.port || e.incoterm) && <span className="flex items-center gap-2 text-ktc-charcoal/75"><MapPin size={14} className="text-ktc-gold" /> {[e.incoterm, e.port].filter(Boolean).join(' · ')}</span>}
                {e.packing && <span className="text-ktc-charcoal/60">Packing: {e.packing}</span>}
              </div>
              {e.message && <p className="text-sm text-ktc-charcoal/70 mt-3 bg-ktc-cream/40 rounded-lg p-3">{e.message}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EnquiriesPanel;
